import { getFeatureFlag, isValidUrl } from "@sinch/feature-flags";
import { getCookie } from "../components/CookieBanner/cookies";

/**
 * CrossDomainStorage allows to re-use cookies between different domains, so
 * for example the marketing website and eu app (.sinch.com) and the australian MM app (.messagemedia.com).
 */
export class CrossDomainStorage {
  private static instance: CrossDomainStorage;
  private readonly IFRAME_ID = "cross-domain-shared-storage";
  private iframe: HTMLIFrameElement | null = null;

  public static readonly COOKIE_KEYS_BLACKLIST = ["cd_token", "cdp_token"];

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private constructor() {}

  private async getIframe(): Promise<HTMLIFrameElement> {
    if (this.iframe) {
      return this.iframe;
    }

    const existingFrame = window.document.getElementById(this.IFRAME_ID);
    if (existingFrame && existingFrame instanceof HTMLIFrameElement) {
      this.iframe = existingFrame;
      return existingFrame;
    }

    return new Promise<HTMLIFrameElement>((res) => {
      const iframeSrc = getFeatureFlag(
        "crossDomainSharedStorage",
        isValidUrl,
        "https://shared-storage.app.sinch.com"
      );

      const iframe = window.document.createElement("iframe");
      iframe.id = this.IFRAME_ID;
      iframe.src = iframeSrc;
      iframe.style.display = "none";
      iframe.onload = () => {
        this.iframe = iframe;
        res(iframe);
      };
      window.document.body.appendChild(iframe);
    });
  }

  public static getInstance(): CrossDomainStorage {
    if (!CrossDomainStorage.instance) {
      CrossDomainStorage.instance = new CrossDomainStorage();
    }
    return CrossDomainStorage.instance;
  }

  public async append(key: string, value: string): Promise<void> {
    const targetIframe = await this.getIframe();

    if (targetIframe.contentWindow) {
      targetIframe.contentWindow.postMessage(
        {
          key,
          action: "append",
          value,
        },
        "*"
      );
    }
  }

  // Settings this as private because we want to prevent users reading the cross-domain storage directly
  // and instead use the copyCookiesToLocal method and get the values from document.cookie
  private async get(key: string): Promise<string> {
    const targetIframe = await this.getIframe();

    return new Promise<string>((res, rej) => {
      if (!targetIframe.contentWindow) {
        return rej("no iframe contentWindow");
      }
      const timeoutId = setTimeout(() => rej("timeout"), 1000);
      window.addEventListener(
        "message",
        function listener(e: MessageEvent<unknown>) {
          const { data } = e;
          if (
            !!data &&
            typeof data === "object" &&
            "key" in data &&
            typeof data.key === "string" &&
            data.key === key &&
            "value" in data &&
            typeof data.value === "string"
          ) {
            clearTimeout(timeoutId);
            res(data.value);
            window.removeEventListener("message", listener);
          }
        }
      );

      targetIframe.contentWindow.postMessage(
        {
          key,
          action: "get",
        },
        "*"
      );
    });
  }

  public async copyCookiesToLocal(cookieKeys: string[]) {
    for (const key of cookieKeys) {
      if (CrossDomainStorage.COOKIE_KEYS_BLACKLIST.includes(key)) {
        continue; // Skip blacklisted keys
      }
      try {
        const sharedCookieValue = await this.get(key);
        const localCookieValue = getCookie(key);
        if (sharedCookieValue && !localCookieValue) {
          document.cookie = `${key}=${sharedCookieValue};`;
        }
      } catch (error) {
        console.error("Failed to sync cross-domain storage cookie", key, error);
      }
    }
  }
}
